import type React from "react";
import {useCallback} from "react";
import {useNavigate, useParams} from "react-router-dom";

import {useLevelV2} from "~/api/hooks/useLevelV2";
import CustomButton from "~/component/common/CustomButton";
import LevelStats from "~/component/common/LevelStats";
import MedalDisplay from "~/component/common/MedalDisplay";
import useLevelLoader from "~/contexts/loader/useLevelLoader";

interface PlaylistItemProps {
  levelId: string;
}

const LevelPreview: React.FC<PlaylistItemProps> = ({levelId}) => {
  const navigate = useNavigate();
  const {playlistId} = useParams<{playlistId: string}>();
  const {data: levelData, isLoading} = useLevelV2({id: levelId});
  const levelLoader = useLevelLoader();

  // On hover/touch, trigger level preview.
  const handleHover = useCallback(() => {
    if (levelData) {
      levelLoader(levelId, levelData);
    }
  }, [levelData, levelId, levelLoader]);

  // When the item is clicked, navigate to the level play page.
  const handleClick = useCallback(() => {
    navigate(`/playlist/${playlistId}/play/${levelId}`);
  }, [navigate, playlistId, levelId]);

  return (
    <CustomButton
      onClick={handleClick}
      onMouseEnter={handleHover}
      onTouchStart={handleHover}
      className="w-[200px] bg-gradient-to-b from-black/50 to-indigo-900/50 rounded-lg p-2 shadow-sm transition duration-300 hover:from-black/90 hover:to-indigo-900/90 text-left"
    >
      <h2 className="text-white text-lg font-normal mb-1 truncate">
        {isLoading ? "Loading..." : levelData?.name || levelId}
      </h2>
      <MedalDisplay levelId={levelId} />
      <LevelStats levelId={levelId} />
    </CustomButton>
  );
};

export default LevelPreview;
